import { DateTime } from "luxon";

/**
 * Resolve UTC offset (hours) for a local civil datetime in an IANA zone.
 * Uses historical DST rules via Luxon/IANA.
 *
 * @param {{ year: number, month: number, day: number, hour: number, minute: number, second?: number }} local
 * @param {string} ianaZone e.g. "America/New_York"
 * @returns {{ offsetHours: number, abbreviation: string, zoneName: string, valid: boolean, invalidReason?: string }}
 */
export function resolveOffset(local, ianaZone) {
  const zone = String(ianaZone || "").trim();
  if (!zone) {
    return {
      offsetHours: NaN,
      abbreviation: "",
      zoneName: "",
      valid: false,
      invalidReason: "Missing timezone",
    };
  }

  const dt = DateTime.fromObject(
    {
      year: local.year,
      month: local.month,
      day: local.day,
      hour: local.hour,
      minute: local.minute,
      second: local.second ?? 0,
    },
    { zone }
  );

  if (!dt.isValid) {
    return {
      offsetHours: NaN,
      abbreviation: "",
      zoneName: zone,
      valid: false,
      invalidReason: dt.invalidExplanation || dt.invalidReason || "Invalid datetime",
    };
  }

  return {
    offsetHours: dt.offset / 60,
    abbreviation: dt.offsetNameShort || dt.toFormat("ZZZZ"),
    zoneName: dt.zoneName,
    valid: true,
  };
}

/**
 * Format offset hours for display, e.g. UTC-4 or UTC+5.5
 * @param {number} offsetHours
 */
export function formatUtcOffset(offsetHours) {
  if (offsetHours === 0) return "UTC";
  const sign = offsetHours > 0 ? "+" : "-";
  const abs = Math.abs(offsetHours);
  const whole = Math.floor(abs);
  const mins = Math.round((abs - whole) * 60);
  if (mins === 0) return `UTC${sign}${whole}`;
  return `UTC${sign}${whole}:${String(mins).padStart(2, "0")}`;
}
